import { TextArea } from '@shared/components/ui/textarea';
import { Button } from '@shared/components/ui/button';
import { DatePicker } from '@shared/components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { Label } from '@shared/components/ui/shadcn/label';
import { useToast } from '@shared/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMarkProcessed } from '../../hooks';
import { MarkProcessedRequest } from '../../models';

interface MarkProcessedButtonProps {
  isOpen: boolean;
  onClose: () => void;
  fileIds: number[];
}

export const MarkProcessedButton = ({ isOpen, onClose, fileIds }: MarkProcessedButtonProps) => {
  const { t } = useTranslation('codCourierFiles');
  const [bankCollectedAt, setBankCollectedAt] = useState<Date | undefined>(undefined);
  const [notes, setNotes] = useState('');
  const [dateError, setDateError] = useState(false);
  const { markProcessedAsync, isMarking } = useMarkProcessed();
  const { toast } = useToast();

  const handleMarkProcessed = async () => {
    if (fileIds.length === 0) {
      toast({
        title: t('markProcessed.error.title', 'Error al marcar archivos'),
        description: t('markProcessed.error.no_files', 'Debe seleccionar al menos un archivo'),
        variant: 'destructive',
      });
      return;
    }

    // Validar que la fecha sea obligatoria
    if (!bankCollectedAt) {
      setDateError(true);
      toast({
        title: t('markProcessed.error.title', 'Error al marcar archivos'),
        description: t('markProcessed.error.date_required', 'Debe seleccionar una fecha de confirmación en banco'),
        variant: 'destructive',
      });
      return;
    }

    try {
      // Convertir Date a formato fecha para el backend (YYYY-MM-DD)
      const formattedDate = bankCollectedAt.toLocaleDateString('en-CA'); // Formato YYYY-MM-DD

      const data: MarkProcessedRequest = {
        file_ids: fileIds,
        bank_collected_at: formattedDate,
        ...(notes ? { notes } : {}),
      };

      await markProcessedAsync(data);

      onClose();
      setBankCollectedAt(undefined);
      setNotes('');
      setDateError(false);
    } catch {
      // El error ya se maneja en el hook
    }
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setBankCollectedAt(undefined);
      setNotes('');
      setDateError(false);
    }
  }, [isOpen]);

  // Reset error cuando se selecciona una fecha
  useEffect(() => {
    if (bankCollectedAt) {
      setDateError(false);
    }
  }, [bankCollectedAt]);

  const handleClose = () => {
    if (!isMarking) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('markProcessed.modal_title', 'Marcar Archivos como Procesados')}</DialogTitle>
          <DialogDescription>
            {t(
              'markProcessed.modal_description',
              'Se marcarán {{count}} archivo(s) como procesados después de confirmar en banco.',
              {
                count: fileIds.length,
              },
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Fecha de confirmación en banco */}
          <div className="space-y-2">
            <Label htmlFor="bank_collected_at">
              {t('markProcessed.bank_collected_at', 'Fecha de Confirmación en Banco')}{' '}
              <span className="text-destructive">*</span>
            </Label>
            <DatePicker
              id="bank_collected_at"
              date={bankCollectedAt}
              onDateChange={setBankCollectedAt}
              placeholder={t('markProcessed.select_date', 'Seleccionar fecha')}
              disabled={isMarking}
              error={dateError}
            />
          </div>

          {/* Notas */}
          <div className="space-y-2">
            <Label htmlFor="notes">
              {t('markProcessed.notes', 'Notas')} ({t('markProcessed.optional', 'Opcional')})
            </Label>
            <TextArea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('markProcessed.notes_placeholder', 'Ej: Confirmado en banco BBVA')}
              disabled={isMarking}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isMarking}>
            {t('markProcessed.cancel', 'Cancelar')}
          </Button>
          <Button onClick={handleMarkProcessed} disabled={isMarking || fileIds.length === 0 || !bankCollectedAt}>
            {isMarking ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('markProcessed.processing', 'Procesando...')}
              </>
            ) : (
              t('markProcessed.confirm', 'Marcar como Procesado')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
